import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
    createAbsolute,
} from "../../../../Common/ModelPath/ModelPath";
import {
    CrossFitTableAction,
    CrossFitTableActionType,
} from "../../Controls/MultiControls/CrossfitTable/Store/crossFitTableActions";
import {
    findFreeInstanceNumber,
    getInstancesAfterAdd,
    getInstancesAfterRemove,
} from "../../Helpers/StateAccessors/MultilineHelpers";
import { currentViewModelSelect } from "../../Helpers/StateAccessors/ViewModelSelect";
import { BindingPath, ViewModelFieldName } from "../Core/CoreTypes";
import { IReadonlyModelHunk } from "../Core/ModelStoring/IReadonlyModelHunk";
import { ModelHunk } from "../Core/ModelStoring/ModelHunk";
import { ModelAction, ModelActionType } from "../Core/ModelStoring/Store/modelActions";
import { PathUtils } from "../Core/PathUtils/PathUtils";
import { CandyThunkAction } from "../Core/Store/CandyReduxTypes";
import { iterateEntries } from "../../../../Common/IterableUtils";
import { IFormModelAccessor } from "../Form/IFormModelAccessor";
import { IForm } from "../Form/IForm";
import { ModelChunk } from "../Core/ModelStoring/ModelChunk";
import { SortDataType, SortType } from "../../Controls/MultiControls/MultiLine/MultiLineTypes";
import { sortInstances } from "../../Helpers/SortingHelpers";
import { IMultilineManagerClient } from "../Core/Filtering/MultilineManager";
import { GetUserResponse } from "../../../../Common/CandyApiClient/ICandyApiClient";

/*
 * ====================================================
 *
 *          Это свалка -- подлежит разбору!!!
 *
 * ====================================================
 */

export function removeMultilineField(bindingPath: AbsoluteResolvedModelPath, index: string): CandyThunkAction {
    return (_dispatch, getState, form) => {
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        form.setChildren(bindingPath, getInstancesAfterRemove(instances, [index]));
    };
}

export function addMultilineField(bindingPath: AbsoluteResolvedModelPath): CandyThunkAction {
    return (_dispatch, getState, form) => {
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const instance = findFreeInstanceNumber(instances);
        const newInstances = getInstancesAfterAdd(instances, instance, instances.length);
        form.setChildren(bindingPath, newInstances);
    };
}

export function setValues(values: IReadonlyModelHunk, deleted: AbsoluteResolvedModelPath[]): ModelAction {
    return {
        type: ModelActionType.SetValues,
        payload: { values: values, deleted: deleted },
    };
}

export function clearModel(): ModelAction {
    return {
        type: ModelActionType.ClearModel,
    };
}

export function setToModel(
    bindingPath: AbsoluteResolvedModelPath,
    field: ViewModelFieldName,
    value: unknown
): CandyThunkAction {
    return (_dispatch, _getState, form) => {
        form.setViewModelField(bindingPath, field, value);
    };
}

export function setToModelBatch(modelUpdate: ModelHunk): CandyThunkAction {
    return (_dispatch, _getState, form) => {
        form.updateModel(model => {
            for (const [path, viewModel] of modelUpdate.iterateEntries()) {
                for (const [fieldName, fieldValue] of iterateEntries(viewModel)) {
                    model.setViewModelField(path.toAbsolute(), fieldName, fieldValue);
                }
            }
        });
    };
}

export function getChildren(bindingPath: BindingPath | AbsoluteModelPath): CandyThunkAction<readonly string[]> {
    return (_dispatch, getState) => currentViewModelSelect.children(getState(), bindingPath);
}

export function setChildrenToModel(
    path: BindingPath,
    childValues: unknown[],
    field?: ViewModelFieldName
): CandyThunkAction {
    return (_dispatch, _getState, form) => {
        form.updateModel(model => {
            model.setChildren(
                path,
                childValues.map((_, index) => `${index}`)
            );
            for (let index = 0; index < childValues.length; index++) {
                model.setViewModelField(PathUtils.join([path, index.toString()]), field ?? "value", childValues[index]);
            }
        });
    };
}

export function aggregatedSetToModel(
    bindingPath: AbsoluteModelPath | BindingPath,
    field: string,
    value: unknown
): ModelAction {
    return {
        type: ModelActionType.AggregatedSetToModel,
        payload: {
            bindingPath: convertToLegacyPath(bindingPath),
            field: field,
            value: value,
        },
    };
}

export function copySubtree(
    form: IFormModelAccessor,
    sourceBindingPath: BindingPath | AbsoluteResolvedModelPath,
    targetBindingPath: BindingPath | AbsoluteResolvedModelPath,
    doNotCopyPaths?: string[]
): void {
    form.updateModel(model => {
        model.copySubtree(sourceBindingPath, targetBindingPath, doNotCopyPaths);
    });
}

export function revalidateChildrenPaths(parentPath: AbsoluteModelPath): CandyThunkAction<Promise<void>> {
    return async (_d, _s, form: IForm): Promise<void> => {
        const iterableChildrenPaths = form.getChildrenPaths(parentPath);
        await form.revalidatePaths(iterableChildrenPaths);
    };
}

export function removeSingleAttachment(bindingPath: AbsoluteResolvedModelPath): CandyThunkAction {
    return (_dispatch, _getState, form) => {
        form.remove(bindingPath);
    };
}

export function setColGrouping(
    stickyTablePath: string,
    groupKey: undefined | string,
    isShowColumns: boolean,
    doNotThrottle: boolean = false
): CrossFitTableAction {
    return {
        type: CrossFitTableActionType.SetColumnGrouping,
        payload: {
            stickyTablePath: stickyTablePath,
            groupKey: groupKey,
            isShowColumns: isShowColumns,
            doNotThrottle: doNotThrottle,
        },
    };
}

export function setHiddenColGrouping(
    stickyTablePath: string,
    groupKey: string,
    isShowColumns: boolean,
    doNotThrottle: boolean = false
): CrossFitTableAction {
    return {
        type: CrossFitTableActionType.SetHiddenColumnGrouping,
        payload: {
            stickyTablePath: stickyTablePath,
            groupKey: groupKey,
            isShowColumns: isShowColumns,
            doNotThrottle: doNotThrottle,
        },
    };
}

export function setStateHiddenColGrouping(
    stickyTablePath: string,
    groupKey: string,
    isShowColumns: boolean
): CrossFitTableAction {
    return {
        type: CrossFitTableActionType.SetStateHiddenColumnGrouping,
        payload: {
            stickyTablePath: stickyTablePath,
            groupKey: groupKey,
            isShowColumns: isShowColumns,
        },
    };
}

export function sort(
    multilinePath: string,
    dataPath: string,
    dataType: SortDataType,
    dataSave: boolean,
    value: SortType,
    client: IMultilineManagerClient
): CandyThunkAction {
    return (_dispatch, getState, form) => {
        if (dataSave) {
            const state = getState();
            const multilineState = currentViewModelSelect.node(state, multilinePath);
            const multilineSort = { dataPath: dataPath, dataType: dataType, value: value };
            const children = currentViewModelSelect.children(state, multilinePath);
            const sortedChildren = sortInstances(children, multilineState, multilineSort);

            form.setChildren(multilinePath, sortedChildren);
        }
        client.setSorting({ value: value, dataPath: dataPath, dataType: dataType });
    };
}

export function addInstance(
    bindingPath: AbsoluteResolvedModelPath | BindingPath,
    data?: ModelChunk,
    doNotCopyPaths?: string[],
    insertAfterInstance?: string,
    insertBeforeInstance?: string,
    _filteredUniqKey: string = "default"
): CandyThunkAction<string> {
    return (_dispatch, getState, form) => {
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const instance = findFreeInstanceNumber(instances);
        const instanceIndex = insertAfterInstance
            ? instances.indexOf(insertAfterInstance) + 1
            : insertBeforeInstance
              ? instances.indexOf(insertBeforeInstance)
              : instances.length;
        const newInstances = getInstancesAfterAdd(instances, instance, instanceIndex);
        if (instanceIndex !== instances.length && doNotCopyPaths && doNotCopyPaths.length && data) {
            for (const path of doNotCopyPaths) {
                ModelHunk.set(data, path, { value: "" });
            }
        }
        form.setChildren(bindingPath, newInstances);
        if (data) {
            form.setBatchByPrefix(`${bindingPath}/${instance}`, data);
        }
        form.filteringManager.handleAddInstances(
            convertToAbsoluteModelPath(bindingPath),
            [instance],
            insertAfterInstance,
            insertBeforeInstance
        );
        return instance;
    };
}

export function copyInstanceAction(
    bindingPath: BindingPath | AbsoluteResolvedModelPath,
    sourceInstance: string,
    doNotCopyPaths?: string[],
    insertAfterInstance?: string,
    _filteredUniqKey: string = "default"
): CandyThunkAction {
    return (_dispatch, getState, form) => {
        function getInstanceData(): ModelChunk {
            const instancePath = convertToAbsoluteModelPath(bindingPath).joinWith(sourceInstance);
            const dataWithRelativePaths = ModelHunk.create();
            const instanceData = form.getBatch(instancePath);
            const doNotCopyPathsSet = new Set(doNotCopyPaths);

            const pathsToCopy = ModelHunk.paths(instanceData)
                .map<[string, string]>(absolutePath => [
                    absolutePath,
                    absolutePath.replace(`${instancePath.toLegacyPath()}/`, ""),
                ])
                .filter(([, relativePath]) => doNotCopyPaths == undefined || !doNotCopyPathsSet.has(relativePath));

            for (const [absolutePath, relativePath] of pathsToCopy) {
                ModelHunk.set(dataWithRelativePaths, relativePath, ModelHunk.get(instanceData, absolutePath));
            }

            return dataWithRelativePaths;
        }

        const data = getInstanceData();
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const instance = findFreeInstanceNumber(instances);
        const instanceIndex = insertAfterInstance ? instances.indexOf(insertAfterInstance) + 1 : instances.length;
        const newInstances = getInstancesAfterAdd(instances, instance, instanceIndex);
        if (instanceIndex !== instances.length && doNotCopyPaths && doNotCopyPaths.length && data) {
            for (const path of doNotCopyPaths) {
                ModelHunk.set(data, path, { value: "" });
            }
        }
        form.updateModel(model => {
            model.setChildren(bindingPath, newInstances);
            if (data) {
                model.setBatchByPrefix(`${bindingPath}/${instance}`, data);
            }
        });
        form.filteringManager.handleAddInstances(
            convertToAbsoluteModelPath(bindingPath),
            [instance],
            insertAfterInstance
        );
    };
}

export enum LastInstanceBehaviour {
    Remove,
    Clear,
}

export function removeInstance(
    bindingPath: BindingPath | AbsoluteResolvedModelPath,
    instancesToRemove: string,
    lastInstanceBehaviour: LastInstanceBehaviour = LastInstanceBehaviour.Remove,
    filteredUniqKey: string = "default"
): CandyThunkAction {
    return removeInstances(bindingPath, [instancesToRemove], lastInstanceBehaviour, filteredUniqKey);
}

export function removeInstances(
    bindingPath: BindingPath | AbsoluteResolvedModelPath,
    instancesToRemove: readonly string[],
    lastInstanceBehaviour: LastInstanceBehaviour = LastInstanceBehaviour.Remove,
    _filteredUniqKey: string = "default"
): CandyThunkAction {
    return (_dispatch, getState, form) => {
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const instancesAfterRemove = getInstancesAfterRemove(instances, instancesToRemove);
        form.setChildren(bindingPath, instancesAfterRemove);
        removeByPrefix(form, bindingPath, instancesToRemove);
        form.filteringManager.handleRemoveInstances(convertToAbsoluteModelPath(bindingPath), instancesToRemove);
        if (lastInstanceBehaviour === LastInstanceBehaviour.Clear) {
            if (instancesAfterRemove.length === 0) {
                form.setChildren(bindingPath, ["0"]);
                form.filteringManager.handleAddInstances(convertToAbsoluteModelPath(bindingPath), ["0"]);
            }
        }
    };
}

function removeByPrefix(
    form: IForm,
    bindingPath: BindingPath | AbsoluteResolvedModelPath,
    instancesToRemove: readonly string[]
): void {
    const baseInstancePath = typeof bindingPath === "string" ? createAbsolute(bindingPath) : bindingPath;
    const instancesPaths = instancesToRemove.map(instance => baseInstancePath.joinWith(instance));
    form.removeByPrefix(instancesPaths);
}

export function fillMultiline(
    bindingPath: BindingPath | AbsoluteResolvedModelPath,
    values: IReadonlyModelHunk,
    newInstanceCount: number,
    _filteredUniqKey: string = "default"
): CandyThunkAction {
    return (_dispatch, _getState, form) => {
        let instancesAfterAdd: string[] = [];
        form.updateModel(model => {
            let instances = model.getChildren(bindingPath);
            if (instances != undefined) {
                for (let i = 0; i < newInstanceCount; i++) {
                    const instance = findFreeInstanceNumber(instances);
                    instances = [...instances, instance];
                }
                instancesAfterAdd = instances;
                model.setChildren(bindingPath, instances);
            }
            model.setBatchByPrefix(bindingPath, values.toModelChunk());
        });
        if (newInstanceCount !== 0) {
            form.filteringManager.handleAddInstances(convertToAbsoluteModelPath(bindingPath), instancesAfterAdd);
        }
    };
}

export function getPortalUserInfo(): CandyThunkAction<Promise<GetUserResponse>> {
    return async (_, __, form) => await form.getPortalUser();
}
